import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { newItemSelector } from 'src/containers/AddSkuPage/selectors';
import { QUESTION_TYPES } from 'src/containers/AddSkuPage/constants';
import { isStringType } from './utils';
import PreQuestionInput from './PreQuestionInput';
import PreQuestionTextArea from './PreQuestionTextArea';
import PreQuestionDropdown from './PreQuestionDropdown';
import PreQuestionCheckbox from './PreQuestionCheckbox';
import PreQuestionDevice from './PreQuestionDevice';
import PreQuestionMedia from 'src/components/PreQuestion/PreQuestionMedia';
import PreQuestionSubhint from './PreQuestionSubhint';
import {
  answerChange,
  answerQuantityChange,
  deviceAnswerChange,
  deviceInputAnswerChange,
} from './actions';

const PreQuestion = ({ question, sku, error, includeIDK, isEVFlow }) => {
  const dispatch = useDispatch();
  const item = useSelector(newItemSelector);
  const questionId = question.get('id').toString();
  const questionJS = question.toJS();
  const [makeInputValue, setMakeInputValue] = useState('');

  const dropdownValue = item.getIn(['questions', questionId]);
  const inputValue = item.getIn(['questions', questionId, 'text']);
  const checkboxValue = item.getIn(['questions', questionId]);
  const mediaValue = item.getIn(['questions', questionId, 'media']);

  const inputChange = (event) => {
    event.preventDefault();
    dispatch(answerChange({ question: questionJS, value: event.target.value }));
  };

  const mediaChange = (files) => {
    dispatch(answerChange({ question: questionJS, value: files }));
  };


  const dropdownChange = (value) => {
    dispatch(answerChange({ question: questionJS, value }));
  };

  const checkboxChange = (value) => {
    dispatch(
      answerChange({
        question: questionJS,
        value,
      }),
    );
  };

  const checkboxQuantityChange = ({ id, quantity }) => {
    dispatch(
      answerQuantityChange({
        question: questionJS,
        id,
        quantity,
      }),
    );
  };

  /*
  ----------------------------------------------
    START DEVICE CATALOGING onchange & values
  ----------------------------------------------
  */

  const makeValue = item.getIn(['questions', questionId, 'answerValue']);
  const modelValue = item.getIn(['questions', questionId, 'model']);
  const isMakeValueString = isStringType(makeValue);
  const isModelValueString = isStringType(modelValue);

  const onMakeDropdownChange = (value) => {
    /*
      If any change to make dropdown is made,
      reset the local state makeInputValue to ''
    */
    setMakeInputValue('');
    return dispatch(
      deviceAnswerChange({
        question: questionJS,
        questionType: 'makeDropdown',
        makeValue: value,
        modelValue: null,
      }),
    );
  };

  const onModelDropdownChange = (value) => {
    return dispatch(
      deviceAnswerChange({
        questionType: 'modelDropdown',
        question: questionJS,
        makeValue,
        modelValue: value,
      }),
    );
  };

  const onMakeInputChange = (event) => {
    setMakeInputValue(event.target.value);
    dispatch(
      deviceInputAnswerChange({
        questionType: 'makeInput',
        question: questionJS,
        makeInputValue: event.target.value,
        modelInputValue: modelValue,
      }),
    );
  };

  const onModelInputChange = (event) => {
    event.preventDefault();
    /*
      When on edit, check if make input is present.
      If yes, it will send makeInputValue to PreQuestionDevice component which renders inputfield
      If not, render dropdown with make value.
    */
    if (makeInputValue) {
      dispatch(
        deviceInputAnswerChange({
          questionType: 'makeInput',
          question: questionJS,
          makeInputValue,
          modelInputValue: event.target.value,
        }),
      );
    } else {
      dispatch(
        deviceInputAnswerChange({
          questionType: 'modelInput',
          question: questionJS,
          makeInputValue: makeValue,
          modelInputValue: event.target.value,
        }),
      );
    }
  };

  const resetDeviceQuestion = () => {
    /*
      This function triggers when X icon on make input is clicked.
      It resets all values to null.
    */
    dispatch(
      deviceAnswerChange({
        questionType: 'makeDropdown',
        question: questionJS,
        makeValue: null,
        modelValue: null,
      }),
    );
  };
  /*
  ----------------------------------------------
    END DEVICE CATALOGING onchange & values
  ----------------------------------------------
  */

  let questionComponent;
  switch (question.get('inputType')) {
    case QUESTION_TYPES.INPUT:
      questionComponent = (
        <PreQuestionInput
          question={question}
          error={error}
          onChange={inputChange}
          value={inputValue}
        />
      );
      break;
    case QUESTION_TYPES.TEXT_AREA:
      questionComponent = (
        <PreQuestionTextArea
          question={question}
          error={error}
          onChange={inputChange}
          value={inputValue}
        />
      );
      break;
    case QUESTION_TYPES.DROPDOWN:
      questionComponent = (
        <PreQuestionDropdown
          question={question}
          error={error}
          sku={sku}
          onChange={dropdownChange}
          value={dropdownValue}
        />
      );
      break;
    case QUESTION_TYPES.CHECKBOX:
      questionComponent = (
        <PreQuestionCheckbox
          question={question}
          error={error}
          sku={sku}
          onChange={checkboxChange}
          onQuantityChange={checkboxQuantityChange}
          value={checkboxValue}
        />
      );
      break;
    case QUESTION_TYPES.DEVICE:
      questionComponent = (
        <PreQuestionDevice
          question={questionJS}
          error={error}
          value={makeValue} // MAKE VALUE
          modelValue={modelValue} // MODEL VALUE
          makeInputValue={isMakeValueString ? makeValue : makeInputValue} // MAKE INPUT VALUE
          modelInputValue={isModelValueString ? modelValue : null} // MODEL INPUT VALUE
          onMakeDropdownChange={onMakeDropdownChange} // MAKE ONCHANGE
          onModelDropdownChange={onModelDropdownChange} // MODEL ONCHANGE
          onMakeInputChange={onMakeInputChange} // MAKE INPUT ONCHANGE
          onModelInputChange={onModelInputChange} // MODEL INPUT ONCHANGE
          onIconClick={resetDeviceQuestion}
          {...typeof includeIDK === 'boolean' && { includeIDK }} // ev suppressees IDK
          {...typeof isEVFlow === 'boolean' && { isEVFlow }} // ev is needed for nonEditable to bone out
        />
      );
      break;
    case QUESTION_TYPES.MEDIA:
      questionComponent = (
        <PreQuestionMedia
          question={question}
          error={error}
          onChange={mediaChange}
          value={mediaValue}
        />
      );
      break;
    default:
      questionComponent = null;
  }
  return (
    <div id={question.get('id')}>
      {questionComponent}
      <PreQuestionSubhint question={question} />
    </div>
  );
};

PreQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  sku: PropTypes.object.isRequired,
  error: PropTypes.string,
  includeIDK: PropTypes.bool,
  isEVFlow: PropTypes.bool,
  deviceDefaultVales: PropTypes.object,
};

export default PreQuestion;
